import { hc } from "hono/client";
import type { z } from "zod";

import {
  ApiErrorSchema,
  AppDetailResponseSchema,
  AppPreferenceResultSchema,
  AppPreferenceUpdateSchema,
  AppsResponseSchema,
  LaunchRequestSchema,
  LaunchResponseSchema,
  LoginRequestSchema,
  MeResponseSchema,
  PlatformHealthResponseSchema,
  ToolRequestCreatedSchema,
  ToolRequestSubmissionSchema,
  WorkspaceAccessUpdateSchema,
  WorkspaceAdminResponseSchema,
  WorkspaceAppearanceUpdateSchema,
  WorkspaceEntryBrandingResponseSchema,
  WorkspaceFaviconResultSchema,
  WorkspaceLogoResultSchema,
  WorkspaceMemberAccessResultSchema,
  WorkspaceMemberAccessUpdateSchema,
  WorkspaceMemberCreatedSchema,
  WorkspaceMemberCreateSchema,
  WorkspaceMutationResultSchema,
  WorkspaceSettingsUpdateSchema,
  WorkspaceSubdomainResultSchema,
  WorkspaceSubdomainUpdateSchema,
  WorkspaceSubdomainViewSchema,
  WorkspaceToolAccessResultSchema,
  WorkspaceToolUpdateSchema,
  WorkspaceTransitionRequestSchema,
  type AppDetailResponse,
  type AppPreferenceResult,
  type AppPreferenceUpdate,
  type AppsResponse,
  type LaunchResponse,
  type LoginRequest,
  type MeResponse,
  type PlatformHealthResponse,
  type ToolRequestCreated,
  type ToolRequestSubmission,
  type WorkspaceAccessUpdate,
  type WorkspaceAdminResponse,
  type WorkspaceAppearanceUpdate,
  type WorkspaceEntryBrandingResponse,
  type WorkspaceFaviconResult,
  type WorkspaceLogoResult,
  type WorkspaceMemberAccessResult,
  type WorkspaceMemberAccessUpdate,
  type WorkspaceMemberCreated,
  type WorkspaceMemberCreate,
  type WorkspaceMutationResult,
  type WorkspaceSettingsUpdate,
  type WorkspaceSubdomainResult,
  type WorkspaceSubdomainUpdate,
  type WorkspaceSubdomainView,
  type WorkspaceToolAccessResult,
  type WorkspaceToolUpdate,
  DesktopCapabilityChallengeCreatedSchema,
  DesktopCapabilityChallengeRequestSchema,
  type DesktopCapabilityChallengeRequest,
} from "../../shared/schemas";
import type { AppType } from "../../worker/app";

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
  }
}

const rpc = hc<AppType>("/", {
  init: {
    credentials: "include",
  },
});

async function parseResponse<TSchema extends z.ZodType>(
  response: Response,
  schema: TSchema,
): Promise<z.output<TSchema>> {
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const parsedError = ApiErrorSchema.safeParse(payload);
    throw new ApiClientError(
      response.status,
      parsedError.success ? parsedError.data.error.code : "unexpected_error",
      parsedError.success
        ? parsedError.data.error.message
        : "CCBox could not complete the request.",
    );
  }
  return schema.parse(payload);
}

export async function login(input: LoginRequest): Promise<MeResponse> {
  const validated = LoginRequestSchema.parse(input);
  const response = await rpc.api.session.login.$post({ json: validated });
  return parseResponse(response, MeResponseSchema);
}

export async function getWorkspaceEntryBranding(): Promise<WorkspaceEntryBrandingResponse> {
  return parseResponse(
    await rpc.api["workspace-entry"].$get(),
    WorkspaceEntryBrandingResponseSchema,
  );
}

export async function logout(): Promise<void> {
  const response = await rpc.api.session.logout.$post();
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const parsed = ApiErrorSchema.safeParse(payload);
    throw new ApiClientError(
      response.status,
      parsed.success ? parsed.data.error.code : "logout_failed",
      parsed.success ? parsed.data.error.message : "CCBox could not sign you out.",
    );
  }
}

export function navigateToWorkspace(organizationId: string, intendedPath = "/"): void {
  const input = WorkspaceTransitionRequestSchema.parse({
    organizationId,
    intendedPath,
  });
  const form = document.createElement("form");
  form.method = "post";
  form.action = "/api/workspace-transitions";
  form.hidden = true;
  form.dataset["ccboxNavigation"] = "workspace";
  for (const [name, value] of Object.entries(input)) {
    const field = document.createElement("input");
    field.type = "hidden";
    field.name = name;
    field.value = value;
    form.append(field);
  }
  document.body.append(form);
  form.requestSubmit();
}

export async function getMe(): Promise<MeResponse> {
  return parseResponse(await rpc.api.me.$get(), MeResponseSchema);
}

export async function getApps(organizationId: string): Promise<AppsResponse> {
  const response = await rpc.api.organizations[":organizationId"].apps.$get({
    param: { organizationId },
  });
  return parseResponse(response, AppsResponseSchema);
}

export async function updateAppPreference(
  appInstanceId: string,
  input: AppPreferenceUpdate,
): Promise<AppPreferenceResult> {
  const payload = AppPreferenceUpdateSchema.parse(input);
  const response = await rpc.api.apps[":appInstanceId"].preferences.$patch({
    param: { appInstanceId },
    json: payload,
  });
  return parseResponse(response, AppPreferenceResultSchema);
}

export async function submitToolRequest(input: ToolRequestSubmission): Promise<ToolRequestCreated> {
  const payload = ToolRequestSubmissionSchema.parse(input);
  const response = await rpc.api["tool-requests"].$post({
    json: payload,
  });
  return parseResponse(response, ToolRequestCreatedSchema);
}

export async function getWorkspace(organizationId: string): Promise<WorkspaceAdminResponse> {
  const response = await rpc.api.workspaces[":organizationId"].$get({
    param: { organizationId },
  });
  return parseResponse(response, WorkspaceAdminResponseSchema);
}

export async function createWorkspaceMember(
  organizationId: string,
  input: WorkspaceMemberCreate,
): Promise<WorkspaceMemberCreated> {
  const payload = WorkspaceMemberCreateSchema.parse(input);
  const response = await rpc.api.workspaces[":organizationId"].members.$post({
    param: { organizationId },
    json: payload,
  });
  return parseResponse(response, WorkspaceMemberCreatedSchema);
}

export async function updateWorkspaceMemberAccess(
  organizationId: string,
  membershipId: string,
  input: WorkspaceMemberAccessUpdate,
): Promise<WorkspaceMemberAccessResult> {
  const payload = WorkspaceMemberAccessUpdateSchema.parse(input);
  const response = await rpc.api.workspaces[":organizationId"].members[":membershipId"].access.$put(
    {
      param: { organizationId, membershipId },
      json: payload,
    },
  );
  return parseResponse(response, WorkspaceMemberAccessResultSchema);
}

export async function updateWorkspace(
  organizationId: string,
  input: WorkspaceSettingsUpdate,
): Promise<WorkspaceMutationResult> {
  const payload = WorkspaceSettingsUpdateSchema.parse(input);
  const response = await rpc.api.workspaces[":organizationId"].$patch({
    param: { organizationId },
    json: payload,
  });
  return parseResponse(response, WorkspaceMutationResultSchema);
}

export async function updateWorkspaceAppearance(
  organizationId: string,
  input: WorkspaceAppearanceUpdate,
): Promise<WorkspaceMutationResult> {
  const payload = WorkspaceAppearanceUpdateSchema.parse(input);
  const response = await rpc.api.workspaces[":organizationId"].appearance.$patch({
    param: { organizationId },
    json: payload,
  });
  return parseResponse(response, WorkspaceMutationResultSchema);
}

export async function updateWorkspaceSubdomain(
  organizationId: string,
  input: WorkspaceSubdomainUpdate,
): Promise<WorkspaceSubdomainResult> {
  const payload = WorkspaceSubdomainUpdateSchema.parse(input);
  const response = await rpc.api.workspaces[":organizationId"].subdomain.$patch({
    param: { organizationId },
    json: payload,
  });
  return parseResponse(response, WorkspaceSubdomainResultSchema);
}

export async function getWorkspaceSubdomain(
  organizationId: string,
): Promise<WorkspaceSubdomainView> {
  const response = await rpc.api.workspaces[":organizationId"].subdomain.$get({
    param: { organizationId },
  });
  return parseResponse(response, WorkspaceSubdomainViewSchema);
}

export async function updateWorkspaceTool(
  organizationId: string,
  appInstanceId: string,
  input: WorkspaceToolUpdate,
): Promise<WorkspaceMutationResult> {
  const payload = WorkspaceToolUpdateSchema.parse(input);
  const response = await rpc.api.workspaces[":organizationId"].tools[":appInstanceId"].$patch({
    param: { organizationId, appInstanceId },
    json: payload,
  });
  return parseResponse(response, WorkspaceMutationResultSchema);
}

export async function updateWorkspaceToolAccess(
  organizationId: string,
  appInstanceId: string,
  membershipId: string,
  input: WorkspaceAccessUpdate,
): Promise<WorkspaceToolAccessResult> {
  const payload = WorkspaceAccessUpdateSchema.parse(input);
  const response = await rpc.api.workspaces[":organizationId"].tools[":appInstanceId"].access[
    ":membershipId"
  ].$patch({
    param: { organizationId, appInstanceId, membershipId },
    json: payload,
  });
  return parseResponse(response, WorkspaceToolAccessResultSchema);
}

export async function uploadWorkspaceLogo(
  organizationId: string,
  file: File,
): Promise<WorkspaceLogoResult> {
  const contentType = file.type || (file.name.toLowerCase().endsWith(".avif") ? "image/avif" : "");
  const response = await fetch(`/api/workspaces/${encodeURIComponent(organizationId)}/logo`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": contentType },
    body: file,
  });
  return parseResponse(response, WorkspaceLogoResultSchema);
}

export async function removeWorkspaceLogo(organizationId: string): Promise<WorkspaceLogoResult> {
  const response = await fetch(`/api/workspaces/${encodeURIComponent(organizationId)}/logo`, {
    method: "DELETE",
    credentials: "include",
  });
  return parseResponse(response, WorkspaceLogoResultSchema);
}

export async function uploadWorkspaceFavicon(
  organizationId: string,
  file: File,
): Promise<WorkspaceFaviconResult> {
  const contentType =
    file.type ||
    (file.name.toLowerCase().endsWith(".ico")
      ? "image/x-icon"
      : file.name.toLowerCase().endsWith(".avif")
        ? "image/avif"
        : "");
  const response = await fetch(`/api/workspaces/${encodeURIComponent(organizationId)}/favicon`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": contentType },
    body: file,
  });
  return parseResponse(response, WorkspaceFaviconResultSchema);
}

export async function removeWorkspaceFavicon(
  organizationId: string,
): Promise<WorkspaceFaviconResult> {
  const response = await fetch(`/api/workspaces/${encodeURIComponent(organizationId)}/favicon`, {
    method: "DELETE",
    credentials: "include",
  });
  return parseResponse(response, WorkspaceFaviconResultSchema);
}

export async function getApp(appInstanceId: string): Promise<AppDetailResponse> {
  const response = await rpc.api.apps[":appInstanceId"].$get({
    param: { appInstanceId },
  });
  return parseResponse(response, AppDetailResponseSchema);
}

export async function getPlatformVersion(): Promise<PlatformHealthResponse> {
  const response = await fetch("/health", {
    cache: "no-store",
    credentials: "same-origin",
  });
  return parseResponse(response, PlatformHealthResponseSchema);
}

export async function launchApp(
  appInstanceId: string,
  intendedPath: string,
): Promise<LaunchResponse> {
  const payload = LaunchRequestSchema.parse({ intendedPath });
  const response = await rpc.api.apps[":appInstanceId"].launch.$post({
    param: { appInstanceId },
    json: payload,
  });
  return parseResponse(response, LaunchResponseSchema);
}

export async function createDesktopCapabilityChallenge(input: DesktopCapabilityChallengeRequest) {
  const payload = DesktopCapabilityChallengeRequestSchema.parse(input);
  const response = await fetch("/api/desktop/capability-challenges", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return parseResponse(response, DesktopCapabilityChallengeCreatedSchema);
}
